import React, { useState, useCallback, useEffect, useRef } from "react";
import cn from "classnames";
import Person from "./Person";
import Backdrops from "./Backdrops";
import Remix from "./Remix";
import Menus from "./Menus";
import Cursor from "./Cursor";

const storageSettings = localStorage.getItem('sapiens');
let parsed = false;
if (storageSettings !== null) {
  parsed = JSON.parse(storageSettings);
}

const INIT_REMIX = {
  head: parsed ? parsed.head : 6,
  top: parsed ? parsed.top : 3,
  bottom: parsed ? parsed.bottom : 3,
  gender: parsed ? parsed.gender : "female",
  option: parsed ? parsed.option : "cycling",
  style: parsed ? parsed.style : "colorful",
  backdrop: parsed ? parsed.backdrop : 2
};

const Character = ({
  history,
  onChange = () => { },
  setLoading = () => { },
}) => {
  const [backdropsPosition, setBackdropsPosition] = useState({});
  const [remixPosition, setRemixPosition] = useState({});

  const [mouseDown, setMouseDown] = useState(false);
  const [mouseUp, setMouseUp] = useState(false);
  const [position, setPosition] = useState({});

  const [backdropsOpen, setShowBackdrops] = useState(false);
  const [remixOpen, setShowRemix] = useState(false);

  const [backgroundOver, setBackgroudOver] = useState(false);
  const [characterOver, setCharacterOver] = useState(false);

  const [backdropsOver, setBackdropsOver] = useState(false);
  const [remixOver, setRemixOver] = useState(false);

  const handleClick = () => {
    setShowBackdrops(false);
    setShowRemix(false);
  }

  const handleClickStop = e => {
    e.stopPropagation();
  }

  const handleMouseDown = (e) => {
    setMouseDown(true);
    setMouseUp(false);
  }

  const handleMouseUp = () => {
    setMouseDown(false);
    setMouseUp(true);
  }

  const handleMouseMove = (e) => {
    setPosition({ x: e.pageX, y: e.pageY });
  }

  const handleShowBackdrops = () => {
    backgroundOver &&
      setShowBackdrops(true);
    setShowRemix(false);
    setBackdropsPosition({
      x: position.x,
      y: position.y
    });
  }

  const handleShowRemix = () => {
    setShowBackdrops(false);
    setShowRemix(true);
    setRemixPosition({
      x: position.x,
      y: position.y
    });
  }

  const url = history.location.pathname;

  const urlRemix = url.charAt(1) && url.charAt(3) && url.charAt(5) ? {
    head: url.charAt(1),
    top: url.charAt(3),
    bottom: url.charAt(5)
  } : {
    head: INIT_REMIX.head,
    top: INIT_REMIX.top,
    bottom: INIT_REMIX.bottom
  }

  let urlGender
  switch (url.charAt(2)) {
    case "m":
      urlGender = "male";
      break;
    case "f":
      urlGender = "female";
      break;
    default:
      urlGender = INIT_REMIX.gender;
      break;
  }

  let urlOption
  switch (url.charAt(6)) {
    case "w":
      urlOption = "walking";
      break;
    case "r":
      urlOption = "running";
      break;
    case "s":
      urlOption = "standing";
      break;
    case "c":
      urlOption = "cycling";
      break;
    default:
      urlOption = INIT_REMIX.option;
      break;
  }

  let urlStyle;
  switch (url.charAt(4)) {
    case "c":
      urlStyle = "colorful";
      break;
    case "d":
      urlStyle = "duotone";
      break;
    case "o":
      urlStyle = "outline";
      break;
    default:
      urlStyle = INIT_REMIX.style;
      break;
  }

  const urlBackdrop = url.charAt(7) ? url.charAt(7) : INIT_REMIX.backdrop;

  // builder methods
  const [remix, setRemix] = useState(urlRemix);
  const [gender, setGender] = useState(urlGender);
  const [option, setOption] = useState(urlOption);
  const [style, setStyle] = useState(urlStyle);
  const [backdrop, setBackdrop] = useState(urlBackdrop);

  // dynamic assets
  const head = `/images/${gender}/head/${style}/${remix?.head}.svg`;
  const top = `/images/${gender}/top/${style}/${option}/${remix?.top}.svg`;
  const bottom = `/images/${gender}/bottom/${style}/${option}/${remix?.bottom}.svg`;
  const backdropAsset = `/images/backdrops/${style}/${backdrop}.svg`;

  const handleRemix = useCallback((value) => {
    setRemix(value);
  }, []);

  const handleBackdrop = (backdrop) => {
    setBackdrop(backdrop);
  }

  // Use useRef to track loaded images to avoid re-render loops
  const imgsLoadedRef = useRef(new Set());
  const [loadingComplete, setLoadingComplete] = useState(false);

  // Handle URL and settings updates
  useEffect(() => {
    // sapiens.ui8.net / 1m3c5w0

    // - 1st char → head#(0 - 8)
    // - 2nd char → Gender(m / f)
    // - 3rd char → top#(0 - 7)
    // - 4th char → Style(c / d / o)
    // - 5th char → Bottom#(0 - 7)
    // - 6th char → Pose(w / r / s / c)
    // - 7th char → Backdrop#(0 - 8)

    history.push({
      pathname: remix?.head + gender.charAt(0) + remix?.top + style.charAt(0) +
        remix?.bottom + option.charAt(0) + backdrop
    });

    onChange({ head, top, bottom, backdropAsset });

    const settings = {
      'head': remix?.head,
      'top': remix?.top,
      'bottom': remix?.bottom,
      'gender': gender,
      'option': option,
      'style': style,
      'backdrop': backdrop
    };

    localStorage.setItem('sapiens', JSON.stringify(settings));
  }, [history, gender, option, style, remix, head, top, bottom,
    backdrop, backdropAsset, onChange]);

  // Separate effect for image preloading
  useEffect(() => {
    const imgs = [];

    // Collect images to preload
    for (let index = 0; index <= 8; index++) {
      const imgBackdropPreview = `/images/backdrops/${style}/previews/${index}.svg`;
      if (!imgsLoadedRef.current.has(imgBackdropPreview)) {
        imgs.push(imgBackdropPreview);
      }

      const imgBackdrop = `/images/backdrops/${style}/${index}.svg`;
      if (!imgsLoadedRef.current.has(imgBackdrop)) {
        imgs.push(imgBackdrop);
      }
    }

    for (let index = 1; index <= 9; index++) {
      const imgHead = `/images/${gender}/head/${style}/${index}.svg`;
      if (!imgsLoadedRef.current.has(imgHead)) {
        imgs.push(imgHead);
      }
    }

    for (let index = 1; index <= 8; index++) {
      const imgTop = `/images/${gender}/top/${style}/${option}/${index}.svg`;
      if (!imgsLoadedRef.current.has(imgTop)) {
        imgs.push(imgTop);
      }

      const imgBottom = `/images/${gender}/bottom/${style}/${option}/${index}.svg`;
      if (!imgsLoadedRef.current.has(imgBottom)) {
        imgs.push(imgBottom);
      }
    }

    // Only preload if there are new images
    if (imgs.length === 0) {
      if (!loadingComplete && imgsLoadedRef.current.size > 40) {
        setLoadingComplete(true);
        setLoading(false);
        const preloader = document.querySelector('.preloader');
        if (preloader) {
          preloader.style.display = "none";
        }
      }
      return;
    }

    // Preload images
    let loadedCount = 0;
    const totalToLoad = imgs.length;

    imgs.forEach(item => {
      const image = new Image();
      image.onload = () => {
        imgsLoadedRef.current.add(item);
        loadedCount++;

        // Check if all images are loaded and threshold is met
        if (loadedCount === totalToLoad && imgsLoadedRef.current.size > 40 && !loadingComplete) {
          setLoadingComplete(true);
          setLoading(false);
          const preloader = document.querySelector('.preloader');
          if (preloader) {
            preloader.style.display = "none";
          }
        }
      };
      image.onerror = () => {
        loadedCount++;
        // Still count as processed even on error
        if (loadedCount === totalToLoad && imgsLoadedRef.current.size > 40 && !loadingComplete) {
          setLoadingComplete(true);
          setLoading(false);
          const preloader = document.querySelector('.preloader');
          if (preloader) {
            preloader.style.display = "none";
          }
        }
      };
      image.src = item;
    });
  }, [gender, option, style, setLoading, loadingComplete]);

  return (
    <>
      <div
        className="container"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        <div
          className={cn("canvas", {
            "hide-cursor": backgroundOver || characterOver
          })}
          onClick={(e) => handleClickStop(e)}>
          <div className="canvas__background"
            onClick={handleShowBackdrops}
            onMouseEnter={() => setBackgroudOver(true)}
            onMouseLeave={() => setBackgroudOver(false)}></div>
          <div className="canvas__character"
            onMouseEnter={() => setCharacterOver(true)}
            onMouseLeave={() => setCharacterOver(false)}>
            <div className="canvas__sections"
              onClick={handleShowRemix}>
              <Person
                gender={gender}
                option={option}
                remix={remix}
                backdropAsset={backdropAsset}
                head={head}
                top={top}
                bottom={bottom}
              />
            </div>
          </div>
        </div>

        <Backdrops
          mouse={backdropsPosition}
          visible={backdropsOpen}
          onClick={(e) => handleClickStop(e)}
          onMouseEnter={() => setBackdropsOver(true)}
          onMouseLeave={() => setBackdropsOver(false)}
          backdrop={backdrop}
          style={style}
          onChange={handleBackdrop}
          onHide={() => setShowBackdrops(false)}
        />

        <Remix
          mouse={remixPosition}
          visible={remixOpen}
          onClick={(e) => handleClickStop(e)}
          onMouseEnter={() => setRemixOver(true)}
          onMouseLeave={() => setRemixOver(false)}
          remix={remix}
          head={head}
          top={top}
          bottom={bottom}
          onChange={handleRemix}
          onHide={() => setShowRemix(false)} />

      </div>

      <Menus
        gender={gender}
        setGender={setGender}
        option={option}
        setOption={setOption}
        style={style}
        setStyle={setStyle} />

      <Cursor
        x={position.x}
        y={position.y}
        mouseDown={mouseDown}
        mouseUp={mouseUp}
        backgroundOver={backgroundOver}
        characterOver={characterOver}
        backdropsOver={backdropsOver}
        backdropsOpen={backdropsOpen}
        remixOver={remixOver}
        remixOpen={remixOpen} />

    </>
  );
};

export default Character;